import { createFileRoute, Link } from "@tanstack/react-router";
import { ListIcon, UsersIcon } from "lucide-react";
import { pick } from "radashi";
import { useEffect } from "react";
import { toast } from "sonner";
import * as v from "valibot";

import { Button } from "@/components/ui/button";
import { Icon, IconName, IconPicker } from "@/components/ui/icon-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SectionMenuChevron,
  SectionMenuContent,
  SectionMenuItem,
} from "@/components/ui/section-menu";
import { useAppForm } from "@/components/ui/tanstack-form";
import { useCurrentBoard } from "@/features/tasks/store/board.store";
import { useZero } from "@/hooks/use-zero";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/w/$slug/settings/boards/$boardId/")(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  const board = useCurrentBoard((b) => pick(b, ["id", "boardKey", "lists"]));

  if (!board) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="mx-3 text-lg font-medium">Board Settings</h3>
      <BoardForm />

      <SectionMenuContent>
        <SectionMenuItem
          left={UsersIcon}
          right={
            <>
              <span>1 member</span>
              <SectionMenuChevron />
            </>
          }
          description="Manage team members"
        >
          Members
        </SectionMenuItem>
        <SectionMenuItem
          asChild
          left={ListIcon}
          right={<SectionMenuChevron />}
          description={board.lists.map((l) => l.name).join(", ")}
        >
          <Link from={Route.fullPath} to="./lists">
            Lists
          </Link>
        </SectionMenuItem>
      </SectionMenuContent>
    </div>
  );
}

const BOARD_KEY_MAX_LENGTH = 8;
const boardFormSchema = v.object({
  name: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(40)),
  icon: v.pipe(v.string(), v.trim(), v.minLength(1)),
  boardKey: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(1, "Board key is required"),
    v.maxLength(
      BOARD_KEY_MAX_LENGTH,
      `Shouldn't be longer than ${BOARD_KEY_MAX_LENGTH} characters`,
    ),
  ),
});

function BoardForm() {
  const board = useCurrentBoard((b) =>
    pick(b, ["id", "boardKey", "name", "icon"]),
  );

  const zero = useZero();
  const form = useAppForm({
    defaultValues: {
      name: board.name,
      icon: board.icon,
      boardKey: board.boardKey,
    },
    validators: {
      onChange: boardFormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        await zero.mutate.boards.update({
          id: board.id,
          ...value,
        }).server;
        formApi.reset(value, { keepDefaultValues: true });
      } catch (err) {
        console.error("Failed to update board", err);
        toast.error("Failed to update board");
        formApi.reset();
      }
    },
  });

  useEffect(() => {
    if (!form.state.isDirty) {
      form.reset(board, { keepDefaultValues: true });
    }
  }, [board]);

  return (
    <form.AppForm>
      <form
        className="p-3 bg-card text-card-foreground rounded-lg"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="board-name-input" className="ml-1">
              Icon & Name
            </Label>
            <div className="flex gap-3">
              <form.AppField
                name="icon"
                children={(field) => (
                  <IconPicker
                    asChild
                    value={field.state.value as IconName}
                    onValueChange={(val) => field.handleChange(val)}
                    onBlur={field.handleBlur}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                    >
                      <Icon
                        name={field.state.value as IconName}
                        className="size-5"
                      />
                    </Button>
                  </IconPicker>
                )}
              />

              <form.AppField
                name="name"
                children={(field) => (
                  <Input
                    id="board-name-input"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    autoComplete="off"
                    data-1p-ignore
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="board-key-input" className="ml-1">
              Identifier
            </Label>
            <form.AppField
              name="boardKey"
              children={(field) => (
                <Input
                  id="board-key-input"
                  maxLength={BOARD_KEY_MAX_LENGTH}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value
                        .toLocaleUpperCase()
                        .replace(/[^A-Z0-9]/g, ""),
                    )
                  }
                  onBlur={field.handleBlur}
                  autoComplete="off"
                  data-1p-ignore
                />
              )}
            />
          </div>
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isDirty]}
          children={([canSubmit, isDirty]) => (
            <div
              className={cn(
                "grid transition-[grid-template-rows]",
                isDirty ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                <div className="mt-3 flex gap-2 justify-end pt-3 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => form.reset()}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={!canSubmit}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        />
      </form>
    </form.AppForm>
  );
}
